import {reporter} from 'common/report/error-reporting';

export type ScopeTranslations = Record<string, string>;
/** The translations of the scopes bundled together, keyed by scope id and then by key relative to the scope. */
type TranslationBundle = Record<string, ScopeTranslations>;
/** Imports the module holding a language's translations of the scope. */
type TranslationSource = () => Promise<{default: TranslationBundle}>;
export type ScopeTranslationLoaders = Readonly<Record<string, TranslationSource>>;
type ScopeRegistrationListener = (scopeId: string) => void;

const loadersByScope = new Map<string, ScopeTranslationLoaders>();
const bundleLoadsByCacheKey = new Map<string, Promise<TranslationBundle>>();
const registrationListeners = new Set<ScopeRegistrationListener>();

function bundleCacheKey(language: string, scopeId: string) {
  return `${language}\u0000${scopeId}`;
}

function isSameLoaders(first: ScopeTranslationLoaders, second: ScopeTranslationLoaders) {
  const languages = Object.keys(first);
  return (
    languages.length === Object.keys(second).length &&
    languages.every((language) => first[language] === second[language])
  );
}

/**
 * Called by `I18nScope.registerTranslationLoaders`: `loaders` gives the source of the scope's machine translations
 * per language.
 */
export function registerScopeTranslationLoaders(scopeId: string, loaders: ScopeTranslationLoaders) {
  const previousLoaders = loadersByScope.get(scopeId);
  loadersByScope.set(scopeId, loaders);
  if (previousLoaders && !isSameLoaders(previousLoaders, loaders)) {
    // a re-evaluated module (Vite HMR) has new loaders that would otherwise hit the cached bundles
    for (const language of Object.keys(previousLoaders)) {
      bundleLoadsByCacheKey.delete(bundleCacheKey(language, scopeId));
    }
  }
  for (const listener of registrationListeners) {
    listener(scopeId);
  }
}

/** Calls `listener` whenever a scope registers, e.g. because a route chunk was evaluated. */
export function onScopeTranslationLoadersRegistered(listener: ScopeRegistrationListener) {
  registrationListeners.add(listener);
}

function flatten(bundle: TranslationBundle): ScopeTranslations {
  const flattened: ScopeTranslations = {};
  for (const [scopeId, translations] of Object.entries(bundle)) {
    for (const [key, text] of Object.entries(translations)) {
      flattened[`${scopeId}.${key}`] = text;
    }
  }
  return flattened;
}

/** Loads a scope's bundle for a language. */
function loadBundle(scopeId: string, language: string): Promise<TranslationBundle> {
  const source = loadersByScope.get(scopeId)?.[language];
  if (!source) {
    return Promise.resolve({});
  }
  const cacheKey = bundleCacheKey(language, scopeId);
  let load = bundleLoadsByCacheKey.get(cacheKey);
  if (!load) {
    load = source()
      .then((module) => module.default)
      .catch((error) => {
        // a failed load renders English for these scopes and is retried the next time one of them is requested
        bundleLoadsByCacheKey.delete(cacheKey);
        reporter.error(`Failed to load ${language} translations of ${scopeId}`, error);
        return {};
      });
    bundleLoadsByCacheKey.set(cacheKey, load);
  }
  return load;
}

/** Loads the translations of the given scopes in a language, for callers to add to i18next in one go. */
export async function loadScopesTranslations(scopeIds: Iterable<string>, language: string): Promise<ScopeTranslations> {
  const bundles = await Promise.all([...scopeIds].map((scopeId) => loadBundle(scopeId, language)));
  const flattenedBundles = bundles.map((bundle) => flatten(bundle));
  return Object.assign({}, ...flattenedBundles);
}

export function loadAllRegisteredScopeTranslations(language: string): Promise<ScopeTranslations> {
  return loadScopesTranslations(loadersByScope.keys(), language);
}
